import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { Checkbox } from "../components/ui/checkbox";
import { Logo } from "../components/Logo";
import { CheckCircle, Printer, Download, PenLine, Trash2 } from "lucide-react";
import { NDADocument, NDADisclosure } from "../components/NDADocument";

interface LocationState {
  firstName:    string;
  lastName:     string;
  email:        string;
  phone:        string;
  isBusiness:   boolean;
  businessName: string;
}

// ─── Signature Pad ────────────────────────────────────────────────────────────

function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing   = useRef(false);
  const lastPos   = useRef<{ x: number; y: number } | null>(null);
  const [hasStrokes, setHasStrokes] = useState(false);

  // Scale CSS-pixel coords to internal canvas coords so drawing is accurate
  // at any display width (fixes misaligned strokes on non-native-size canvases).
  function getPos(e: MouseEvent | TouchEvent, canvas: HTMLCanvasElement) {
    const rect  = canvas.getBoundingClientRect();
    const src   = "touches" in e ? e.touches[0] : e;
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top)  * scaleY,
    };
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    ctx.strokeStyle = "#101828";
    ctx.lineWidth   = 3;
    ctx.lineCap     = "round";
    ctx.lineJoin    = "round";

    const c = canvas;
    function start(e: MouseEvent | TouchEvent) {
      e.preventDefault();
      drawing.current = true;
      const pos = getPos(e, c);
      lastPos.current = pos;
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    }
    function move(e: MouseEvent | TouchEvent) {
      if (!drawing.current) return;
      e.preventDefault();
      const pos = getPos(e, c);
      // Quadratic smoothing between last and current point
      if (lastPos.current) {
        const mx = (lastPos.current.x + pos.x) / 2;
        const my = (lastPos.current.y + pos.y) / 2;
        ctx.quadraticCurveTo(lastPos.current.x, lastPos.current.y, mx, my);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(mx, my);
      }
      lastPos.current = pos;
      setHasStrokes(true);
    }
    function stop() {
      if (!drawing.current) return;
      drawing.current = false;
      lastPos.current = null;
      onChange(c.toDataURL("image/png"));
    }

    canvas.addEventListener("mousedown",  start, { passive: false });
    canvas.addEventListener("mousemove",  move,  { passive: false });
    canvas.addEventListener("mouseup",    stop);
    canvas.addEventListener("mouseleave", stop);
    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove",  move,  { passive: false });
    canvas.addEventListener("touchend",   stop);
    return () => {
      canvas.removeEventListener("mousedown",  start);
      canvas.removeEventListener("mousemove",  move);
      canvas.removeEventListener("mouseup",    stop);
      canvas.removeEventListener("mouseleave", stop);
      canvas.removeEventListener("touchstart", start);
      canvas.removeEventListener("touchmove",  move);
      canvas.removeEventListener("touchend",   stop);
    };
  }, [onChange]);

  function clear() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d")!.clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
    onChange(null);
  }

  return (
    <div className="max-w-lg mx-auto space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-[#364153] uppercase tracking-wide">
          Signature <span className="text-[#9CA3AF] font-normal normal-case tracking-normal">(optional)</span>
        </p>
        {hasStrokes && (
          <button
            type="button"
            onClick={clear}
            className="flex items-center gap-1.5 text-xs text-[#364153] border border-[#E5E7EB] px-3 py-1.5 rounded hover:bg-[#F9FAFB] transition-colors"
          >
            <Trash2 className="w-3 h-3" /> Clear Signature
          </button>
        )}
      </div>
      <div className="relative border border-[#E5E7EB] rounded-md bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          width={800}
          height={280}
          className="w-full touch-none cursor-crosshair"
          style={{ height: "160px" }}
        />
        {!hasStrokes && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="flex items-center gap-2 text-sm text-[#D1D5DB]">
              <PenLine className="w-4 h-4" /> Draw your signature here
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── NDA Page ─────────────────────────────────────────────────────────────────

export function NDAPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const formData  = location.state as LocationState | null;

  const [agreed,    setAgreed]    = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [loading,   setLoading]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error,     setError]     = useState<string | null>(null);

  if (!formData) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <p className="text-[#364153] mb-4">No request data found. Please start from the beginning.</p>
          <button
            onClick={() => navigate("/confidential")}
            className="bg-[#7B1A1A] text-white px-6 py-2.5 rounded hover:bg-[#6b1515] transition-colors text-sm"
          >
            Start Request
          </button>
        </div>
      </div>
    );
  }

  const fullName   = `${formData.firstName} ${formData.lastName}`;
  const acceptedAt = new Date().toLocaleString("en-ZA", {
    day: "2-digit", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit", timeZoneName: "short",
  });

  const handleSubmit = async () => {
    if (!agreed) return;
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeout    = setTimeout(() => controller.abort(), 15000);

    try {
      const res = await fetch("/api/requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          firstName:    formData.firstName,
          lastName:     formData.lastName,
          email:        formData.email,
          phone:        formData.phone,
          ndaAccepted:  true,
          isBusiness:   formData.isBusiness,
          businessName: formData.isBusiness ? formData.businessName : null,
          signature:    signature ?? undefined,
        }),
      });
      let data: { success?: boolean; message?: string } = {};
      try { data = await res.json(); } catch {
        throw new Error(`Server error (${res.status}). Make sure the backend is running.`);
      }
      if (!res.ok) throw new Error(data.message || `Request failed (${res.status}).`);
      setSubmitted(true);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "AbortError") {
        setError("Request timed out. Please check that the backend is running and try again.");
      } else {
        setError(err instanceof Error ? err.message : "An unexpected error occurred.");
      }
    } finally {
      clearTimeout(timeout);
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
        <nav className="bg-white border-b border-[#E5E7EB] px-8 py-4 flex items-center gap-2">
          <Logo className="w-14" />
          <span className="text-sm font-medium text-[#101828]">Leo Group</span>
        </nav>
        <div className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="max-w-md w-full bg-white border border-[#E5E7EB] rounded-lg p-10 text-center">
            <CheckCircle className="w-12 h-12 text-[#7B1A1A] mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-[#101828] mb-3">NDA Accepted. Request Submitted.</h1>
            <p className="text-[#364153] mb-2">
              Thank you, <strong>{formData.firstName}</strong>. Your NDA has been accepted and access request received.
            </p>
            <p className="text-sm text-[#4A5565] mb-8">
              Our team will review your submission. You will be contacted at{" "}
              <strong>{formData.email}</strong> once a decision has been made.
            </p>
            <button onClick={() => navigate("/")} className="text-sm text-[#7B1A1A] hover:underline">
              Return to homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nda-page-wrapper min-h-screen bg-[#F9FAFB]">

      {/* Nav */}
      <nav className="print-hide bg-white border-b border-[#E5E7EB] px-8 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center gap-2">
          <Logo className="w-14" />
          <span className="text-sm font-medium text-[#101828]">Leo Group</span>
        </button>
        <span className="text-xs text-[#4A5565]">NDA &amp; Acceptance</span>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-10">

        {/* Step indicator */}
        <div className="print-hide flex items-center gap-2 mb-8 max-w-md">
          <div className="flex items-center gap-1.5">
            <span className="w-6 h-6 rounded-full bg-[#E5E7EB] text-[#4A5565] text-xs flex items-center justify-center font-semibold">✓</span>
            <span className="text-sm text-[#4A5565]">Your Details</span>
          </div>
          <div className="flex-1 h-px bg-[#7B1A1A]" />
          <div className="flex items-center gap-1.5">
            <span className="w-6 h-6 rounded-full bg-[#7B1A1A] text-white text-xs flex items-center justify-center font-semibold">2</span>
            <span className="text-sm font-medium text-[#7B1A1A]">NDA &amp; Acceptance</span>
          </div>
        </div>

        {/* Page header */}
        <div className="print-hide mb-6">
          <p className="text-xs uppercase tracking-widest text-[#7B1A1A] font-semibold mb-2">Legal Gate — Mandatory Acceptance</p>
          <h1 className="text-3xl font-semibold text-[#101828] mb-2">Non-Disclosure Agreement</h1>
        </div>

        {/* 1 — NDA Content */}
        <div className="bg-white border border-[#E5E7EB] rounded-lg shadow-sm mb-4">
          <div className="nda-scroll-container p-8 sm:p-10 overflow-y-auto" style={{ maxHeight: "560px" }}>
            <NDADocument
              acceptedBy={agreed ? fullName : undefined}
              acceptedAt={acceptedAt}
              email={formData.email}
              phone={formData.phone}
              isBusiness={formData.isBusiness}
              businessName={formData.businessName}
              signature={agreed ? signature : undefined}
            />
          </div>
        </div>

        {/* 2 — Acceptance Text */}
        <div className="print-hide bg-white border border-[#E5E7EB] rounded-lg shadow-sm p-6 mb-4">
          <div className="flex items-start gap-3 bg-[#F9FAFB] p-4 rounded border border-[#E5E7EB]">
            <Checkbox
              id="nda-agree"
              checked={agreed}
              onCheckedChange={(v) => setAgreed(v === true)}
            />
            <label htmlFor="nda-agree" className="text-sm text-[#364153] cursor-pointer leading-relaxed">
              I have read and understood this NDA and agree to be legally bound by its terms.
            </label>
          </div>
        </div>

        {/* 3 — Signature Block */}
        <div className="print-hide bg-white border border-[#E5E7EB] rounded-lg shadow-sm p-6 mb-4">
          <SignaturePad onChange={setSignature} />
        </div>

        {/* 4 — Print / Save */}
        <div className="print-hide flex items-center gap-3 mb-4">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 border border-[#E5E7EB] text-[#364153] px-5 py-2.5 rounded text-sm hover:bg-[#F9FAFB] hover:border-[#D1D5DB] transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 border border-[#E5E7EB] text-[#364153] px-5 py-2.5 rounded text-sm hover:bg-[#F9FAFB] hover:border-[#D1D5DB] transition-colors"
          >
            <Download className="w-4 h-4" />
            Save as PDF
          </button>
        </div>

        {/* 5 — Legally Binding Notice */}
        <div className="print-hide bg-white border border-[#E5E7EB] rounded-lg shadow-sm p-6 mb-4 space-y-3">
          <div className="bg-[#FEF3F2] border border-[#7B1A1A]/20 rounded p-4 text-sm text-[#364153] leading-relaxed">
            <strong className="text-[#7B1A1A]">Legally binding agreement.</strong> Electronic acceptance under
            the Electronic Communications and Transactions Act 25 of 2002 (ECTA) carries the same legal weight
            as a handwritten signature.
          </div>
          <p className="text-xs text-[#4A5565]">
            Accepting as:{" "}
            <span className="font-semibold text-[#364153]">{fullName}</span>
            {" · "}{formData.email}
          </p>
        </div>

        {/* 6 — Disclosure */}
        <NDADisclosure />

        {/* 7 — Accept Button */}
        <div className="print-hide bg-white border border-[#E5E7EB] rounded-lg shadow-sm p-6 mt-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSubmit}
              disabled={!agreed || loading}
              className={`flex-1 py-3 rounded font-medium text-sm transition-colors ${
                agreed && !loading
                  ? "bg-[#7B1A1A] hover:bg-[#6b1515] text-white"
                  : "bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed"
              }`}
            >
              {loading ? "Submitting…" : "Accept & Submit Request"}
            </button>
            <button
              onClick={() => navigate("/")}
              className="border border-[#E5E7EB] text-[#364153] px-6 py-3 rounded text-sm hover:bg-[#F9FAFB] transition-colors"
            >
              Decline & Exit
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
